import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Brain,
  Check,
  CircleNotch,
  Compass,
  FloppyDisk,
  Lightbulb,
  PlusCircle,
  ShieldWarning,
  Sparkle,
  UsersThree,
  type Icon,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { SectionShell } from "@/components/therapist/settings/section-primitives";
import type {
  MatchingMethodTag,
  MatchingNotSuitableTag,
  MatchingPopulationTag,
  MatchingProblemTag,
  MatchingStyleTag,
  TherapistMatchingProfile,
} from "@/types/matching";

interface MatchingProfileResponse {
  profile: TherapistMatchingProfile;
  isEmpty: boolean;
}

interface Props {
  endpoint: string;
  queryKey: readonly unknown[];
  title?: string;
  description?: string;
  /** Render without the surrounding card surface + header (when an outer shell already provides them). */
  bare?: boolean;
  /** Tighter spacing, no group icons or coaching copy. For data-dense admin surfaces. */
  dense?: boolean;
}

export const EMPTY_MATCHING_PROFILE: TherapistMatchingProfile = {
  primaryProblemTags: [],
  secondaryProblemTags: [],
  populationTags: [],
  methodTags: [],
  styleTags: [],
  notSuitableTags: [],
};

const PROBLEMS: Array<{ value: MatchingProblemTag; label: string }> = [
  { value: "anxiety", label: "Anksioznost / panika" },
  { value: "depression", label: "Depresivnost" },
  { value: "burnout", label: "Stres / burnout" },
  { value: "trauma", label: "Trauma" },
  { value: "relationships", label: "Partnerski odnosi" },
  { value: "family", label: "Porodični odnosi" },
  { value: "self_esteem", label: "Samopouzdanje" },
  { value: "grief", label: "Tuga / gubitak" },
  { value: "parenting", label: "Roditeljstvo" },
  { value: "addiction", label: "Zavisnosti" },
  { value: "sexual_problems", label: "Seksualni problemi" },
  { value: "gender_identity", label: "Problemi rodnog identiteta" },
  { value: "infidelity", label: "Prevara u odnosu" },
  { value: "friendships", label: "Prijateljski odnosi" },
  { value: "communication", label: "Komunikacija" },
  { value: "uncertainty", label: "Nisam siguran / opšta podrška" },
];

const POPULATIONS: Array<{ value: MatchingPopulationTag; label: string }> = [
  { value: "adult", label: "Odrasli" },
  { value: "adolescent", label: "Adolescenti" },
  { value: "couple_family", label: "Parovi / porodice" },
];

const METHODS: Array<{ value: MatchingMethodTag; label: string }> = [
  { value: "cbt", label: "KBT" },
  { value: "gestalt", label: "Geštalt" },
  { value: "systemic", label: "Sistemska" },
  { value: "emdr", label: "EMDR" },
  { value: "integrative", label: "Integrativna" },
  { value: "counseling", label: "Savjetovanje" },
  { value: "psychodynamic", label: "Psihodinamska" },
  { value: "sex_therapy", label: "Seksualna psihoterapija" },
];

const STYLES: Array<{ value: MatchingStyleTag; label: string }> = [
  { value: "structured", label: "Strukturisan rad" },
  { value: "practical", label: "Praktičan pristup" },
  { value: "supportive", label: "Podržavajući razgovor" },
  { value: "exploratory", label: "Istraživački rad" },
  { value: "emotional_depth", label: "Dubinski emocionalni rad" },
  { value: "homework", label: "Zadaci između sesija" },
];

const NOT_SUITABLE: Array<{ value: MatchingNotSuitableTag; label: string }> = [
  { value: "active_suicide_risk", label: "Aktivni suicidalni rizik" },
  { value: "acute_psychosis", label: "Akutna psihoza" },
  { value: "active_substance_crisis", label: "Aktivna kriza zavisnosti" },
  { value: "minor_without_guardian", label: "Maloljetnik bez staratelja" },
];

function toggle<T extends string>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

export function MatchingProfileEditor({
  endpoint,
  queryKey,
  title = "Matching profil",
  description = "Ovi tagovi pomažu sistemu da predloži terapeuta klijentu nakon upitnika.",
  bare = false,
  dense = false,
}: Props) {
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState<TherapistMatchingProfile>(EMPTY_MATCHING_PROFILE);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async (): Promise<MatchingProfileResponse> => {
      const res = await fetch(endpoint);
      if (!res.ok) throw new Error("Greška pri učitavanju matching profila");
      return res.json();
    },
  });

  useEffect(() => {
    if (data?.profile) setProfile(data.profile);
  }, [data?.profile]);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(endpoint, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profile),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || "Greška pri čuvanju matching profila");
      }
      return res.json() as Promise<MatchingProfileResponse>;
    },
    onSuccess: async (next) => {
      setProfile(next.profile);
      await queryClient.invalidateQueries({ queryKey: [...queryKey] });
      toast.success("Matching profil je sačuvan.");
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Greška pri čuvanju matching profila"),
  });

  const saveButton = (
    <Button
      type="button"
      size={dense ? "sm" : "default"}
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
    >
      {mutation.isPending ? <CircleNotch className="size-4 animate-spin" /> : <FloppyDisk className="size-4" />}
      Sačuvaj matching profil
    </Button>
  );

  const body = isLoading ? (
    <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
      <CircleNotch className="size-4 animate-spin" />
      Učitavanje...
    </div>
  ) : (
    <MatchingProfileFields profile={profile} onChange={setProfile} dense={dense} />
  );

  if (bare) {
    return (
      <div>
        {body}
        {!isLoading && (
          <div
            className={cn(
              "flex justify-end border-t border-border/60",
              dense ? "mt-4 pt-4" : "mt-6 pt-5"
            )}
          >
            {saveButton}
          </div>
        )}
      </div>
    );
  }

  return (
    <SectionShell title={title} description={description} footer={!isLoading ? saveButton : undefined}>
      {body}
    </SectionShell>
  );
}

export function isMatchingProfileEmpty(profile: TherapistMatchingProfile): boolean {
  return Object.values(profile).every((values) => Array.isArray(values) && values.length === 0);
}

/** Controlled tag editor (no fetch, no save button). Used standalone in onboarding. */
export function MatchingProfileFields({
  profile,
  onChange,
  dense = false,
}: {
  profile: TherapistMatchingProfile;
  onChange: (next: TherapistMatchingProfile) => void;
  dense?: boolean;
}) {
  const totalSelected =
    profile.primaryProblemTags.length +
    profile.secondaryProblemTags.length +
    profile.populationTags.length +
    profile.methodTags.length +
    profile.styleTags.length;

  return (
    <div className={dense ? "space-y-3" : "space-y-4"}>
      <div
        className={cn(
          "flex items-start gap-3 border transition-colors",
          dense ? "rounded-lg px-3 py-2.5" : "rounded-2xl px-4 py-3.5",
          totalSelected === 0
            ? "border-amber-200/80 bg-amber-50/70 text-amber-800"
            : "border-primary/20 bg-primary/[0.06] text-foreground",
        )}
      >
        {!dense && (
          <span
            className={cn(
              "mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg",
              totalSelected === 0 ? "bg-amber-100 text-amber-600" : "bg-primary/12 text-primary",
            )}
          >
            {totalSelected === 0 ? <Lightbulb weight="duotone" className="size-4.5" /> : <Sparkle weight="duotone" className="size-4.5" />}
          </span>
        )}
        <p className={cn("leading-relaxed", dense ? "text-xs" : "text-sm")}>
          {totalSelected === 0 ? (
            dense ? (
              <>
                <span className="font-semibold">Još ništa nije označeno.</span> Precizniji tagovi
                daju kvalitetnije preporuke klijentima.
              </>
            ) : (
              <>
                <span className="font-semibold">Još ništa nije označeno.</span> Što preciznije popuniš tagove, to kvalitetniji
                upiti klijenata stižu kroz matching.
              </>
            )
          ) : dense ? (
            <>
              <span className="font-semibold tabular-nums">{totalSelected}</span>{" "}
              {totalSelected === 1 ? "tag označen" : "tagova označeno"}. Sistem ih koristi za
              preporuke klijentima.
            </>
          ) : (
            <>
              <span className="font-semibold tabular-nums">{totalSelected}</span>{" "}
              {totalSelected === 1 ? "tag označen" : "tagova označeno"}. Sistem ovo koristi da te predloži pravim klijentima.
            </>
          )}
        </p>
      </div>

      <OptionGroup
        icon={Compass}
        title="Glavne oblasti rada"
        description="Teme u kojima imaš najviše iskustva. Nose najveću težinu pri preporuci."
        dense={dense}
        options={PROBLEMS}
        values={profile.primaryProblemTags}
        onToggle={(value) =>
          onChange({
            ...profile,
            primaryProblemTags: toggle(profile.primaryProblemTags, value),
            // Tema je ili glavna ili dodatna; glavna pobjeđuje.
            secondaryProblemTags: profile.secondaryProblemTags.filter((tag) => tag !== value),
          })
        }
      />
      <OptionGroup
        icon={PlusCircle}
        title="Dodatne oblasti rada"
        description={
          profile.primaryProblemTags.length > 0
            ? "Teme s kojima radiš, ali nisu primarni fokus. Glavne oblasti se ovdje ne ponavljaju."
            : "Teme s kojima radiš, ali nisu primarni fokus."
        }
        dense={dense}
        options={PROBLEMS.filter((option) => !profile.primaryProblemTags.includes(option.value))}
        values={profile.secondaryProblemTags}
        onToggle={(value) => onChange({ ...profile, secondaryProblemTags: toggle(profile.secondaryProblemTags, value) })}
      />
      <OptionGroup
        icon={UsersThree}
        title="Populacije"
        description="S kojim grupama klijenata radiš."
        dense={dense}
        options={POPULATIONS}
        values={profile.populationTags}
        onToggle={(value) => onChange({ ...profile, populationTags: toggle(profile.populationTags, value) })}
      />
      <OptionGroup
        icon={Brain}
        title="Metode"
        description="Pristupi i tehnike koje primjenjuješ."
        dense={dense}
        options={METHODS}
        values={profile.methodTags}
        onToggle={(value) => onChange({ ...profile, methodTags: toggle(profile.methodTags, value) })}
      />
      <OptionGroup
        icon={Sparkle}
        title="Stil rada"
        description="Kako izgleda tvoj rad sa klijentom."
        dense={dense}
        options={STYLES}
        values={profile.styleTags}
        onToggle={(value) => onChange({ ...profile, styleTags: toggle(profile.styleTags, value) })}
      />
      <OptionGroup
        icon={ShieldWarning}
        title="Ne preporučuj za"
        description="Stanja kod kojih ne želiš biti predložen/a kao terapeut."
        tone="danger"
        dense={dense}
        options={NOT_SUITABLE}
        values={profile.notSuitableTags}
        onToggle={(value) => onChange({ ...profile, notSuitableTags: toggle(profile.notSuitableTags, value) })}
      />
    </div>
  );
}

function OptionGroup<T extends string>({
  icon: GroupIcon,
  title,
  description,
  options,
  values,
  onToggle,
  tone = "default",
  dense = false,
}: {
  icon?: Icon;
  title: string;
  description?: string;
  options: Array<{ value: T; label: string }>;
  values: T[];
  onToggle: (value: T) => void;
  tone?: "default" | "danger";
  dense?: boolean;
}) {
  const danger = tone === "danger";
  const count = values.length;
  return (
    <section
      className={cn(
        "border transition-colors",
        dense ? "rounded-lg p-3" : "rounded-2xl p-4 sm:p-5",
        danger
          ? "border-rose-200/70 bg-rose-50/40"
          : count > 0
            ? cn("border-primary/25 bg-card", !dense && "shadow-sm")
            : "border-border/70 bg-card",
      )}
    >
      <header className={cn("flex items-center", dense ? "gap-2" : "gap-3")}>
        {GroupIcon && !dense && (
          <span
            className={cn(
              "grid size-9 shrink-0 place-items-center rounded-xl",
              danger ? "bg-rose-100 text-rose-600" : "bg-primary/10 text-primary",
            )}
          >
            <GroupIcon weight="duotone" className="size-5" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <h3 className={cn("font-semibold text-foreground", dense ? "text-[13px]" : "text-sm")}>
            {title}
          </h3>
          {description && !dense && (
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
          )}
        </div>
        {count > 0 && (
          <span
            className={cn(
              "shrink-0 rounded-full font-semibold tabular-nums",
              dense ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs",
              danger ? "bg-rose-100 text-rose-700" : "bg-primary/12 text-primary",
            )}
          >
            {count}
          </span>
        )}
      </header>

      <div className={cn("flex flex-wrap", dense ? "mt-2.5 gap-1.5" : "mt-4 gap-2")}>
        {options.map((option) => {
          const checked = values.includes(option.value);
          const base = cn(
            "group/pill inline-flex items-center border font-medium transition-all duration-150 active:scale-[0.97]",
            dense
              ? "gap-1.5 rounded-lg px-2.5 py-1 text-xs"
              : "gap-2 rounded-xl px-3.5 py-2 text-sm",
          );
          const activeClasses = danger
            ? "border-rose-300 bg-rose-100/70 text-rose-700 shadow-sm"
            : "border-primary/70 bg-primary/10 text-primary shadow-sm";
          const idleClasses = danger
            ? "border-border bg-card text-muted-foreground hover:border-rose-200 hover:bg-rose-50/50 hover:text-rose-600"
            : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:bg-primary/[0.04] hover:text-foreground";
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onToggle(option.value)}
              aria-pressed={checked}
              className={cn(base, checked ? activeClasses : idleClasses)}
            >
              <span
                className={cn(
                  "grid shrink-0 place-items-center rounded-full border transition-colors",
                  dense ? "size-3.5" : "size-4",
                  checked
                    ? danger
                      ? "border-rose-500 bg-rose-500 text-white"
                      : "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-transparent text-transparent group-hover/pill:border-current",
                )}
              >
                <Check className={dense ? "size-2.5" : "size-3"} weight="bold" />
              </span>
              {option.label}
            </button>
          );
        })}
      </div>
    </section>
  );
}
